import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ChatBubbleLeftRightIcon, 
  XMarkIcon,
  PaperAirplaneIcon,
  QuestionMarkCircleIcon
} from '@heroicons/react/24/outline';

const SmartAssistant = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      type: 'bot',
      content: "👋 Hi! I'm your FBRSTAPP assistant. How can I help you today?",
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState('');

  const navigationRules = {
    product: {
      keywords: ['product', 'about the product', 'how it works', 'overview', 'what is this'],
      action: 'scroll',
      target: '#product',
      response: "You can explore everything about our product here 👉"
    },
    features: {
      keywords: ['features', 'tools', 'capabilities', 'software options'],
      action: 'scroll',
      target: '#features',
      response: "Check out all our powerful features here 👉"
    },
    pricing: {
      keywords: ['pricing', 'cost', 'subscription', 'plans', 'charges'],
      action: 'scroll',
      target: '#pricing',
      response: "Here's where you can view our plans and pricing 👉"
    },
    benefits: {
      keywords: ['why use this', 'advantages', 'benefits', 'effective', 'reliable', 'fast'],
      action: 'scroll',
      target: '#benefits',
      response: "See why FBRSTAPP is the best choice for your business 👉"
    },
    signup: {
      keywords: ['join', 'signup', 'create account', 'get started'],
      action: 'link',
      target: '/register',
      response: "Join us today and simplify your invoicing journey! 👉"
    },
    support: {
      keywords: ['support', 'technical support', 'need help', 'talk to support'],
      action: 'link',
      target: '/support',
      response: "Need help? Our support team is ready to assist 👉"
    },
    helpCenter: {
      keywords: ['help center', 'documentation', 'guide', 'how-to'],
      action: 'link',
      target: '/help-center',
      response: "Find detailed guides and documentation here 👉"
    },
    contact: {
      keywords: ['contact', 'contact us', 'email', 'phone', 'talk to someone'],
      action: 'link',
      target: '/contact-us',
      response: "Get in touch with our team 👉"
    },
    training: {
      keywords: ['training', 'onboarding', 'learning', 'how do i use it'],
      action: 'link',
      target: '/training',
      response: "Learn how to use FBRSTAPP effectively 👉"
    },
    community: {
      keywords: ['community', 'user group', 'forum', 'ask others'],
      action: 'link',
      target: '/community',
      response: "Join our community of users 👉"
    }
  };

  const quickActions = [
    { label: 'Features', target: '#features', type: 'scroll' },
    { label: 'Pricing', target: '#pricing', type: 'scroll' },
    { label: 'Get Started', target: '/register', type: 'link' },
    { label: 'Support', target: '/support', type: 'link' }
  ];

  const findMatchingRule = (input) => {
    const lowerInput = input.toLowerCase();
    
    for (const [, rule] of Object.entries(navigationRules)) {
      if (rule.keywords.some(keyword => lowerInput.includes(keyword))) {
        return rule;
      }
    }
    
    return null;
  };

  const handleScroll = (target) => {
    const element = document.querySelector(target);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!inputValue.trim()) return;

    // Add user message
    const userMessage = {
      type: 'user',
      content: inputValue,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);

    // Find matching rule
    const rule = findMatchingRule(inputValue);
    
    let botResponse;
    if (rule) {
      if (rule.action === 'scroll') {
        handleScroll(rule.target);
        botResponse = {
          type: 'bot',
          content: rule.response,
          action: rule.action,
          target: rule.target,
          timestamp: new Date()
        };
      } else {
        botResponse = {
          type: 'bot',
          content: rule.response,
          action: rule.action,
          target: rule.target,
          timestamp: new Date()
        };
      }
    } else {
      botResponse = {
        type: 'bot',
        content: "I can help you navigate to different sections of our site. Try asking about features, pricing, getting started, or support!",
        timestamp: new Date()
      };
    }

    setMessages(prev => [...prev, botResponse]);
    setInputValue('');
  };

  const handleQuickAction = (action) => {
    if (action.type === 'scroll') {
      handleScroll(action.target);
      setMessages(prev => [...prev, {
        type: 'bot',
        content: `Taking you to ${action.label} section 👉`,
        timestamp: new Date()
      }]);
    } else {
      setMessages(prev => [...prev, {
        type: 'bot',
        content: `Redirecting you to ${action.label} 👉`,
        action: 'link',
        target: action.target,
        timestamp: new Date()
      }]);
    }
  };

  return (
    <>
      {/* Chat Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 bg-blue-600 text-white p-4 rounded-full shadow-lg hover:bg-blue-700 transition-colors z-50"
          aria-label="Open chat assistant"
        >
          <ChatBubbleLeftRightIcon className="h-6 w-6" />
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 w-96 h-96 bg-white rounded-lg shadow-2xl border z-50 flex flex-col">
          {/* Header */}
          <div className="bg-blue-600 text-white p-4 rounded-t-lg flex justify-between items-center">
            <div className="flex items-center">
              <QuestionMarkCircleIcon className="h-5 w-5 mr-2" />
              <span className="font-semibold">FBRSTAPP Assistant</span>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-white hover:text-gray-200"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 p-4 overflow-y-auto space-y-3">
            {messages.map((message, index) => (
              <div key={index} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-xs p-3 rounded-lg ${
                  message.type === 'user' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  <p className="text-sm">{message.content}</p>
                  {message.action === 'link' && (
                    <Link
                      to={message.target}
                      className="inline-block mt-2 px-3 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600"
                      onClick={() => setIsOpen(false)}
                    >
                      Go there →
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Quick Actions */}
          <div className="p-3 border-t bg-gray-50">
            <p className="text-xs text-gray-600 mb-2">Quick actions:</p>
            <div className="flex flex-wrap gap-1">
              {quickActions.map((action, index) => (
                <button
                  key={index}
                  onClick={() => handleQuickAction(action)}
                  className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded hover:bg-blue-200"
                >
                  {action.label}
                </button>
              ))}
            </div>
          </div>

          {/* Input */}
          <form onSubmit={handleSubmit} className="p-4 border-t">
            <div className="flex space-x-2">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Ask me anything..."
                className="flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
              <button
                type="submit"
                className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <PaperAirplaneIcon className="h-4 w-4" />
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
};

export default SmartAssistant;